import { useState } from "react";
import type { DisciplineSlug } from "@jumpbean/shared";

export interface DraggableItem {
  id: string;
  label: string;
  imageUrl?: string;
  emoji?: string;
}

export interface DropZone {
  id: string;
  label?: string;
  hint?: string;
}

export interface DragDropChallengeContent {
  /** Items that can be dragged/placed */
  items: DraggableItem[];
  /** Drop zones where items can be placed */
  dropZones: DropZone[];
  /** Correct arrangement (dropZone id -> item id) */
  correctArrangement: Record<string, string>;
  /** Instructions for the challenge */
  instructions?: string;
  /** Whether items can be reused */
  allowReuse?: boolean;
}

export interface DragDropChallengeProps {
  /** Challenge content configuration */
  content: DragDropChallengeContent;
  /** Discipline for theming */
  discipline?: DisciplineSlug;
  /** Called when arrangement changes */
  onAnswer: (arrangement: Record<string, string>) => void;
  /** Current arrangement (dropZone id -> item id) */
  currentArrangement: Record<string, string>;
  /** Whether answer has been submitted */
  isSubmitted?: boolean;
  /** Whether the submitted answer was correct */
  isCorrect?: boolean;
}

/**
 * Drag Drop Challenge Component
 * Uses tap-to-select then tap-to-place for child-friendly interaction.
 * Great for word building, spelling, and sequencing challenges.
 */
export function DragDropChallenge({
  content,
  discipline = "spelling",
  onAnswer,
  currentArrangement,
  isSubmitted = false,
  isCorrect = false,
}: DragDropChallengeProps) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // Get items that haven't been placed (unless reuse is allowed)
  const availableItems = content.allowReuse
    ? content.items
    : content.items.filter(
        (item) => !Object.values(currentArrangement).includes(item.id)
      );

  // Handle tapping an item to select it
  const handleItemTap = (itemId: string) => {
    if (isSubmitted) return;
    setSelectedItemId(selectedItemId === itemId ? null : itemId);
  };

  // Handle tapping a drop zone to place selected item
  const handleDropZoneTap = (zoneId: string) => {
    if (isSubmitted || !selectedItemId) return;

    const newArrangement = { ...currentArrangement };

    // If zone already has an item, return it to available pool
    if (newArrangement[zoneId] && !content.allowReuse) {
      // Item goes back to pool
    }

    // Place the selected item
    newArrangement[zoneId] = selectedItemId;
    onAnswer(newArrangement);
    setSelectedItemId(null);
  };

  // Handle removing an item from a drop zone
  const handleRemoveItem = (zoneId: string) => {
    if (isSubmitted) return;
    const newArrangement = { ...currentArrangement };
    delete newArrangement[zoneId];
    onAnswer(newArrangement);
  };

  // Get item by id
  const getItem = (itemId: string): DraggableItem | undefined => {
    return content.items.find((item) => item.id === itemId);
  };

  // Check if a specific drop zone has correct answer
  const isZoneCorrect = (zoneId: string): boolean | null => {
    if (!isSubmitted) return null;
    return currentArrangement[zoneId] === content.correctArrangement[zoneId];
  };

  return (
    <div className="flex flex-col items-center gap-8">
      {content.instructions && (
        <p className="text-center font-body text-body-lg text-neutral-700">
          {content.instructions}
        </p>
      )}

      {/* Drop Zones */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-neutral-200 w-full">
        <div className="flex flex-wrap justify-center gap-3">
          {content.dropZones.map((zone) => {
            const placedItemId = currentArrangement[zone.id];
            const placedItem = placedItemId ? getItem(placedItemId) : null;
            const zoneCorrect = isZoneCorrect(zone.id);

            return (
              <button
                key={zone.id}
                type="button"
                onClick={() =>
                  placedItem
                    ? handleRemoveItem(zone.id)
                    : handleDropZoneTap(zone.id)
                }
                disabled={isSubmitted}
                className={`
                  min-w-[64px] min-h-[72px] px-4
                  rounded-xl border-4 border-dashed
                  flex flex-col items-center justify-center gap-1
                  transition-all duration-200
                  ${getDropZoneStyles(!!selectedItemId, !!placedItem, zoneCorrect, discipline)}
                `}
                aria-label={
                  placedItem
                    ? `Remove ${placedItem.label} from zone`
                    : `Place item in zone ${zone.label || zone.id}`
                }
              >
                {placedItem ? (
                  <>
                    <ItemDisplay item={placedItem} />
                    {!isSubmitted && (
                      <span className="text-xs text-neutral-500">tap to remove</span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-2xl text-neutral-400">
                      {zone.hint || "?"}
                    </span>
                    {zone.label && (
                      <span className="text-xs text-neutral-500">{zone.label}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Available Items */}
      <div className="w-full">
        <h3 className="text-center font-display font-bold text-body-lg text-neutral-600 mb-4">
          {selectedItemId ? "Now tap where to place it!" : "Tap to select"}
        </h3>
        <div className="flex flex-wrap justify-center gap-3">
          {availableItems.map((item) => {
            const isSelected = selectedItemId === item.id;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemTap(item.id)}
                disabled={isSubmitted}
                className={`
                  min-w-[64px] min-h-[64px] px-4
                  rounded-xl border-4
                  flex items-center justify-center
                  transition-all duration-200
                  ${getItemStyles(isSelected, discipline)}
                `}
                aria-label={`Select ${item.label}`}
                aria-pressed={isSelected}
              >
                <ItemDisplay item={item} />
              </button>
            );
          })}

          {availableItems.length === 0 && !isSubmitted && (
            <p className="text-neutral-500 font-body">
              All items placed! Tap a slot to remove an item.
            </p>
          )}
        </div>
      </div>

      {/* Clear all button */}
      {Object.keys(currentArrangement).length > 0 && !isSubmitted && (
        <button
          type="button"
          onClick={() => onAnswer({})}
          className="text-neutral-500 hover:text-neutral-700 font-body text-body-md underline"
        >
          Start over
        </button>
      )}
    </div>
  );
}

/**
 * Display component for draggable items
 */
function ItemDisplay({ item }: { item: DraggableItem }) {
  if (item.imageUrl) {
    return (
      <img
        src={item.imageUrl}
        alt={item.label}
        className="w-10 h-10 object-contain"
      />
    );
  }

  if (item.emoji) {
    return <span className="text-2xl">{item.emoji}</span>;
  }

  return (
    <span className="font-display font-bold text-body-xl text-neutral-800">
      {item.label}
    </span>
  );
}

/**
 * Get styles for drop zone
 */
function getDropZoneStyles(
  hasSelection: boolean,
  hasItem: boolean,
  isCorrect: boolean | null,
  discipline: DisciplineSlug
): string {
  if (isCorrect === true) {
    return `
      bg-meadow-100 border-meadow-500 border-solid
      shadow-[0_2px_0_0_theme(colors.meadow.600)]
    `;
  }

  if (isCorrect === false) {
    return `
      bg-neutral-100 border-neutral-400 border-solid
      opacity-75
    `;
  }

  if (hasItem) {
    return getDisciplineFilledStyles(discipline);
  }

  if (hasSelection) {
    return `
      bg-sunshine-50 border-sunshine-400
      ring-2 ring-sunshine-300 ring-offset-1
      animate-pulse
    `;
  }

  return `
    bg-neutral-50 border-neutral-300
    hover:border-neutral-400 hover:bg-neutral-100
  `;
}

/**
 * Get filled zone styles based on discipline
 */
function getDisciplineFilledStyles(discipline: DisciplineSlug): string {
  const styles: Record<DisciplineSlug, string> = {
    math: `
      bg-luna-light/30 border-luna border-solid
      shadow-[0_2px_0_0_theme(colors.luna.dark)]
    `,
    spelling: `
      bg-petal-light/30 border-petal border-solid
      shadow-[0_2px_0_0_theme(colors.petal.dark)]
    `,
    patterns: `
      bg-prism-yellow/30 border-prism-red border-solid
      shadow-[0_2px_0_0_theme(colors.prism.red.dark)]
    `,
    typing: `
      bg-whiskers-light/30 border-whiskers border-solid
      shadow-[0_2px_0_0_theme(colors.whiskers.dark)]
    `,
    programming: `
      bg-pixel-light/30 border-pixel border-solid
      shadow-[0_2px_0_0_theme(colors.pixel.dark)]
    `,
  };
  return styles[discipline];
}

/**
 * Get styles for available items
 */
function getItemStyles(isSelected: boolean, discipline: DisciplineSlug): string {
  if (isSelected) {
    return `
      ${getDisciplineSelectedStyles(discipline)}
      ring-4 ring-sunshine-400 ring-offset-2
      scale-110
    `;
  }

  return `
    bg-white border-neutral-300 text-neutral-700
    hover:border-neutral-400 hover:bg-neutral-50
    active:scale-95
    shadow-[0_3px_0_0_theme(colors.neutral.300)]
    hover:shadow-[0_1px_0_0_theme(colors.neutral.300)]
  `;
}

/**
 * Get selected styles based on discipline
 */
function getDisciplineSelectedStyles(discipline: DisciplineSlug): string {
  const styles: Record<DisciplineSlug, string> = {
    math: "bg-luna-light border-luna-dark text-neutral-900",
    spelling: "bg-petal-light border-petal-dark text-neutral-900",
    patterns: "bg-prism-yellow border-prism-red text-neutral-900",
    typing: "bg-whiskers-light border-whiskers-dark text-neutral-900",
    programming: "bg-pixel-light border-pixel-dark text-neutral-900",
  };
  return styles[discipline];
}

export default DragDropChallenge;
